import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMobileAuth } from './MobileApp';
import { mobileApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Loader2, Delete, Lock } from 'lucide-react';
import { toast } from 'sonner';

const MobilePinEntry = () => {
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);
  const [lockTimer, setLockTimer] = useState(0);
  
  const { user, verifyPin, logout, getToken } = useMobileAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/mobile';

  useEffect(() => {
    if (locked && lockTimer > 0) {
      const timer = setTimeout(() => setLockTimer(lockTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (locked && lockTimer === 0) {
      setLocked(false);
      setAttempts(0);
    }
  }, [locked, lockTimer]);

  useEffect(() => {
    if (pin.length === 4) {
      handleVerify();
    }
  }, [pin]);

  const handleVerify = async () => {
    if (locked) return;
    
    setLoading(true);
    try {
      const token = getToken();
      await mobileApi.verifyPin(pin, token);
      verifyPin();
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (error) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setPin('');
      
      if (newAttempts >= 5) {
        setLocked(true);
        setLockTimer(300); // 5 minutes
        toast.error('Too many attempts. Locked for 5 minutes.');
      } else {
        toast.error(`Incorrect PIN. ${5 - newAttempts} attempts remaining.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (digit) => {
    if (pin.length < 4 && !locked && !loading) {
      setPin(prev => prev + digit);
    }
  };

  const handleDelete = () => {
    if (!locked && !loading) {
      setPin(prev => prev.slice(0, -1));
    }
  };

  const handleUsePassword = () => {
    logout();
    navigate('/mobile/login');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-[var(--surface-page)] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-xs">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-[var(--surface-sunken)] rounded-full flex items-center justify-center mb-4 text-2xl font-bold text-[var(--volt-icon)]">
            {user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
          </div>
          <h2 className="text-xl font-semibold text-[var(--ink-primary)]">Welcome back</h2>
          <p className="text-[var(--ink-tertiary)]">{user?.name || 'User'}</p>
        </div>

        {/* Lock Message */}
        {locked ? (
          <div className="text-center mb-8">
            <Lock className="w-12 h-12 text-[var(--critical-ink)] mx-auto mb-4" />
            <p className="text-[var(--critical-ink)]">Account temporarily locked</p>
            <p className="text-[var(--ink-tertiary)] text-sm mt-2">Try again in {formatTime(lockTimer)}</p>
          </div>
        ) : (
          <>
            {/* PIN Display */}
            <div className="flex justify-center gap-4 mb-8">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`w-4 h-4 rounded-full transition-all ${
                    pin.length > i
                      ? 'bg-[var(--volt)] scale-110'
                      : 'bg-[var(--surface-pressed)]'
                  }`}
                />
              ))}
            </div>

            {/* Loading indicator */}
            {loading && (
              <div className="flex justify-center mb-4">
                <Loader2 className="w-6 h-6 text-[var(--volt-icon)] animate-spin" />
              </div>
            )}

            {/* Keypad */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                <button
                  key={digit}
                  onClick={() => handleKeyPress(digit.toString())}
                  disabled={loading}
                  className="h-16 rounded-xl bg-[var(--surface-sunken)] hover:bg-[var(--surface-pressed)] active:bg-[var(--surface-pressed)] text-[var(--ink-primary)] text-2xl font-semibold transition-all active:scale-95 disabled:opacity-50"
                >
                  {digit}
                </button>
              ))}
              <div /> {/* Empty cell */}
              <button
                onClick={() => handleKeyPress('0')}
                disabled={loading}
                className="h-16 rounded-xl bg-[var(--surface-sunken)] hover:bg-[var(--surface-pressed)] active:bg-[var(--surface-pressed)] text-[var(--ink-primary)] text-2xl font-semibold transition-all active:scale-95 disabled:opacity-50"
              >
                0
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="h-16 rounded-xl bg-[var(--surface-sunken)] hover:bg-[var(--surface-pressed)] active:bg-[var(--surface-pressed)] text-[var(--ink-primary)] flex items-center justify-center transition-all active:scale-95 disabled:opacity-50"
              >
                <Delete className="w-6 h-6" />
              </button>
            </div>
          </>
        )}

        {/* Use Password Instead */}
        <Button
          variant="ghost"
          onClick={handleUsePassword}
          className="w-full text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)]"
        >
          Use email & password instead
        </Button>
      </div>
    </div>
  );
};

export default MobilePinEntry;
